import { camelCase } from 'lodash';
import { milesToMeters } from '@provider-directory/functions/distance';
import {
  DEFAULT_SEARCH_PARAMS,
  ProvidersRequestFacetEnum,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';
import { toFacetValues } from '@provider-directory/types/search.model';

export const getProvidersSearchParams = (
  request: Partial<ProvidersSearchCriteria>,
): URLSearchParams => {
  const params = new URLSearchParams();

  Object.entries(request).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (key === 'specialties') {
          params.append('specialties[]', item);
        } else {
          params.append(key, item);
        }
      });
    } else if (value) {
      params.append(key, value.toString());
    }
  });

  return params;
};

export const getProvidersSearchPath = (
  customer: string,
  orgAgnostic = false,
): string => (orgAgnostic ? '/v3/providers' : `/v2/${customer}/providers`);

export const getProviderDetailsPath = (
  customer: string,
  npi: string,
  orgAgnostic = false,
): string =>
  orgAgnostic ? `/v3/providers/${npi}` : `/v2/${customer}/providers/${npi}`;

export const getProvidersFacetsPath = (
  customer: string,
  orgAgnostic = false,
): string => (orgAgnostic ? '/v3/providers' : `/v1/${customer}/providers`);

type DistanceUnits = 'miles' | 'meters';
interface SanitizeSearchParamsOptions {
  defaultMaxDistance?: number;
  units?: DistanceUnits;
  /** When false/undefined, timeOfDay and daysOfWeek are stripped (flag off). */
  isTimeDayAvailabilityFilterEnabled?: boolean;
  /** When true with TOD/DOW, request soft/semi-matches (DIR-3581 Flag 4). */
  isTimeDaySemiMatchesEnabled?: boolean;
}

export const sanitizeSearchParams = (
  searchParams: ProvidersSearchCriteria,
  options: SanitizeSearchParamsOptions,
): ProvidersSearchCriteria => {
  const sanitizedParams: Record<string, string | string[]> = {};

  Object.entries(searchParams).forEach(([key, value]) => {
    const searchKey = camelCase(key);
    if (typeof value === 'string' || typeof value === 'number') {
      if (searchKey === 'centerPoint') {
        const [latitude, longitude] = value.toString().split(',');
        sanitizedParams.latitude = latitude;
        sanitizedParams.longitude = longitude;
        if (
          !sanitizedParams.maxDistance &&
          options.defaultMaxDistance !== undefined
        ) {
          sanitizedParams.maxDistance = valueInUnits(
            options.defaultMaxDistance,
            options.units,
          ).toString();
        }
        return;
      }

      if (searchKey === 'maxDistance') {
        sanitizedParams.maxDistance = valueInUnits(
          +value,
          options.units,
        ).toString();
        return;
      }
    }

    if (searchKey === 'specialties') {
      // Special case for specialties because it can contain both specialties
      // and synonyms. A single URL occurrence arrives as a scalar string,
      // not an array (DIR-2090), so normalise before assigning.
      sanitizedParams.unified = toFacetValues(value as string | string[]);
      return;
    }

    // When canOnlineSchedule (UI checkbox) is checked, translate it to allowsOpenScheduling + hasSlots for the backend
    // and keep canOnlineSchedule on the wire so soft pass can distinguish user vs auto (DIR-3581 AC4).
    if (searchKey === 'canOnlineSchedule') {
      if (value) {
        sanitizedParams.allowsOpenScheduling = value;
        sanitizedParams.hasSlots = value;
        sanitizedParams.canOnlineSchedule = value;
      }
      return;
    }

    // Time/day hard filters (DIR-3563): only when flag on; pass through and auto-enable online scheduling
    if (searchKey === 'timeOfDay' || searchKey === 'daysOfWeek') {
      if (!options.isTimeDayAvailabilityFilterEnabled) {
        return;
      }
      const values = Array.isArray(value)
        ? value.filter(Boolean)
        : value
          ? [value]
          : [];
      if (values.length > 0) {
        sanitizedParams[searchKey] = values;
        sanitizedParams.allowsOpenScheduling = 'true';
        sanitizedParams.hasSlots = 'true';
      }
      return;
    }

    // Use original value for all other valid params
    if (
      ['page', 'sort', 'providerName'].includes(searchKey) ||
      Object.keys(ProvidersRequestFacetEnum.enum).includes(searchKey)
    ) {
      sanitizedParams[searchKey] = value;
    }
  });

  const hasTimeDay =
    (Array.isArray(sanitizedParams.timeOfDay) &&
      sanitizedParams.timeOfDay.length > 0) ||
    (Array.isArray(sanitizedParams.daysOfWeek) &&
      sanitizedParams.daysOfWeek.length > 0);

  if (
    options.isTimeDayAvailabilityFilterEnabled &&
    options.isTimeDaySemiMatchesEnabled &&
    hasTimeDay
  ) {
    sanitizedParams.includeSemiMatches = 'true';
  }

  return {
    ...DEFAULT_SEARCH_PARAMS,
    ...sanitizedParams,
  };
};

// This assumes value is always in miles
const valueInUnits = (value: number, unit?: DistanceUnits): number => {
  return unit === 'meters' ? milesToMeters(value) : value;
};
